import React from 'react';

import { ParkFeature } from '@havenengineering/module-haven-content';
import classNames from 'classnames';
import styles from './gallery-preview.module.scss';
import ContentfulPicture from '../../../widget/contentful-picture';
import { HandleAccommodationGalleryGeneral } from '../../../hook/use-accommodation-gallery/use-accommodation-gallery';
import { filterHighlightCarouselItems } from '../../../../../helpers/carouselItems';
import { SearchResultImage } from '../../../../../types/v3/api-response';
import { getAccommodationProductsAmplitude } from '../../../../../helpers/gtm/gtm-shared';
import { Image } from '../../../../caravan-details-page/types';
import { getCaravanInfoAmplitude } from '../../../../../helpers/gtm/gtm-caravan-search';
import { CaravanElastic } from '../../../../caravan-search-page/types';

import { searchResultCardGridProfiles } from '../../../../../constant/image-profiles';
import { AggregatedAccommodationType } from '../../../../aggregating-by-grade/type/types';
import { DataLayerProduct } from '../../../../../types/analytics';

type Props = {
  handleAccommodationGallery?: HandleAccommodationGalleryGeneral;
  item: AggregatedAccommodationType;
  parkFeatures?: ParkFeature[];
  caravanSearch?: boolean;
  listName?: string;
  aggregatedSearch?: boolean;
  highlighted?: boolean;
  soldOut?: boolean;
  showDepositPills?: boolean;
};

export const GalleryPreview = ({
  handleAccommodationGallery,
  item,
  parkFeatures,
  caravanSearch,
  listName,
  aggregatedSearch,
  highlighted = false,
  soldOut = false,
  showDepositPills,
}: Props) => {
  const {
    floorPlan,
    minDeposit,
    matterPortId,
    parkCode,
    parkName,
    accommodationType,
    accommodationDescription,
    financeOption,
    caravanImages,
  } = item;

  const deposit = showDepositPills ? minDeposit || financeOption?.deposit : undefined;

  const getGalleryDataLayerProducts = () => {
    if (aggregatedSearch) return [];
    if (caravanSearch) {
      return [getCaravanInfoAmplitude(item as unknown as CaravanElastic, { list: listName })];
    }

    return getAccommodationProductsAmplitude(item, true);
  };

  const handleGalleryClick = (activeTab: 'accommodation' | 'matterport') => {
    const floorPlanImage: string =
      typeof floorPlan !== 'string' ? JSON.stringify(floorPlan) : floorPlan;

    handleAccommodationGallery?.({
      activeTab,
      accommodationName: accommodationDescription || '',
      caravanImages: caravanSearch ? (caravanImages as Image[]) : (caravanImages as Image[]) || [],
      matterPortId: matterPortId || '',
      floorPlan: floorPlanImage || '',
      parkCode: parkCode || '',
      parkName: parkName || '',
      parkFeatures: filteredParkFeatures || [],
      accomGrade: accommodationDescription || '',
      accommodationType: accommodationType || '',
      products: (getGalleryDataLayerProducts() as DataLayerProduct[]) || [],
      isCaravanSearch: caravanSearch,
      caravanImagesSource: (caravanImages as Image[])[0]?.imageSource || '',
    });
  };

  const filteredParkFeatures = filterHighlightCarouselItems(parkFeatures);

  const image = caravanImages?.[0] as SearchResultImage;

  return (
    <div
      data-testid="gallery-preview"
      className={classNames(styles.galleryPreviewRoot, {
        [styles.highlighted]: highlighted,
        [styles.soldOut]: soldOut,
      })}
    >
      <button
        className={classNames(styles.image, { [styles.highlighted]: highlighted })}
        data-testid="gallery-preview-btn"
        type="button"
        onClick={() => handleGalleryClick('accommodation')}
        disabled={!caravanImages?.length && !matterPortId && !floorPlan}
      >
        {deposit && (
          <span className={styles.depositMessagePill}>{`£${deposit} deposit available`}</span>
        )}
        <span className={styles.iconContainer}>
          <span className={classNames(styles.icon, styles.galleryIcon)}>
            {caravanImages?.length > 1 ? <span>{`+${caravanImages?.length - 1}`}</span> : ''}
          </span>
          <span className="srOnly">Open gallery</span>
        </span>
        <ContentfulPicture
          className={styles.asset}
          url={image?.url}
          profiles={searchResultCardGridProfiles}
          alt={image?.altText || ''}
        />
      </button>
    </div>
  );
};
